import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, CheckCircle, Save, ShoppingCart } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CommunityAssessmentData } from '@/types/entities/clients/assessment';
import {
  communityAssessmentService,
  pricingCommunityAssessmentService,
} from '@/services/entities/clients';
import {
  CommunityProfile,
  FinancialAssessment,
  PainPointsAnalysis,
  TechnologyAssessment,
  StakeholderMapping,
  ImplementationReadiness,
  VisionGoals,
  AddOnsConfirmation,
} from '../../../../onboarding/customer/pricing-page/silver-gold/steps';
import { DiscoveryCallScheduler } from '@/components/entities/community_subpanel-elect';
import { DiscoveryCallBooking } from '@/services/entities/community_subpanel-elect/discoveryCallService';
import ProposalGenerator from '../ProposalGenerator';
import { MONTANA_ADDONS_CATALOG } from '@/data/addons/catalog.montana';

const TOTAL_STEPS = 8; // add-ons confirmation added as step 8
const TOTAL_STEPS_WITH_SCHEDULING = 10; // Including discovery call scheduling and proposal generation

const stepTitles = [
  'Community Profile',
  'Financial Position',
  'Current Challenges',
  'Technology & Systems',
  'Stakeholder Mapping',
  'Implementation Readiness',
  'Vision & Goals',
  'Confirm Add-ons',
  'Schedule Discovery Call',
  'Generate Proposal',
];

export const AssessmentWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tier = (searchParams.get('tier') || 'silver') as 'silver' | 'gold' | 'custom';

  // Detect if this assessment came from pricing flow
  const isPricingFlow =
    searchParams.get('tier') !== null &&
    (tier === 'silver' || tier === 'gold' || tier === 'custom');

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [discoveryCallData, setDiscoveryCallData] = useState<DiscoveryCallBooking | undefined>();
  const [assessmentData, setAssessmentData] = useState<Partial<CommunityAssessmentData>>({
    selectedTier: tier,
  });

  // Load saved progress on mount
  useEffect(() => {
    // Pull add-ons from URL if present
    const addonsParam = searchParams.get('addons');
    if (addonsParam) {
      const ids = addonsParam
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      setAssessmentData((prev) => ({ ...prev, selectedAddOns: ids }));
    }

    const savedProgress = communityAssessmentService.loadProgress();
    if (savedProgress) {
      setAssessmentData(savedProgress.data);
      setCurrentStep(savedProgress.step);
    }
  }, [searchParams]);

  // Ensure page starts at the top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-save progress
  useEffect(() => {
    if (Object.keys(assessmentData).length > 1) {
      // More than just selectedTier
      communityAssessmentService.saveProgress(assessmentData, currentStep);
    }
  }, [assessmentData, currentStep]);

  // Scroll to top when step changes (for better UX)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(`/onboarding/welcome/${tier}`);
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
      return;
    }
    if (currentStep === TOTAL_STEPS && !assessmentCompleted) {
      handleSubmit();
      return;
    }
    if (currentStep === TOTAL_STEPS && assessmentCompleted) {
      // After completion user should go to discovery call scheduling (step 9), not skip straight to proposal generation.
      setCurrentStep(9);
      return;
    }
  };

  const handleSubmit = async () => {
    const missing = getMissingRequired();
    if (missing.length) {
      setValidationErrors(missing);
      // Scroll to top of card to show errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setValidationErrors([]);
    setIsSubmitting(true);
    try {
      if (isPricingFlow) {
        // Pricing flow: save to pricing_assessments table
        await pricingCommunityAssessmentService.submitAssessment(
          assessmentData as CommunityAssessmentData,
        );
      } else {
        // Regular onboarding flow: use existing mock service (TASK-1 unchanged)
        await communityAssessmentService.submitAssessment(
          assessmentData as CommunityAssessmentData,
        );
      }

      // Mark assessment as completed in localStorage for scheduler access
      localStorage.setItem(
        'assessment_progress',
        JSON.stringify({
          completed: true,
          data: assessmentData,
          completedAt: new Date().toISOString(),
        }),
      );

      setAssessmentCompleted(true);
      // Move specifically to discovery call scheduling (step 9)
      setCurrentStep(9);
    } catch (error) {
      console.error('Assessment submission failed:', error);
      setValidationErrors(['Assessment submission failed. Please retry.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscoveryCallScheduled = (booking: DiscoveryCallBooking) => {
    setDiscoveryCallData(booking);
    setCurrentStep(9); // Move to proposal generation
  };

  const handleProposalComplete = (proposalId: string) => {
    // Navigate to proposal view or completion page
    navigate(`/proposals/${proposalId}`);
  };

  const updateAssessmentData = (stepData: Partial<CommunityAssessmentData>) => {
    setAssessmentData((prev) => ({ ...prev, ...stepData }));
  };

  const getMissingRequired = (): string[] => {
    const required: { field: keyof CommunityAssessmentData; label: string }[] = [
      { field: 'communityName', label: 'Community Name' },
      { field: 'contactName', label: 'Primary Contact Name' },
      { field: 'contactEmail', label: 'Primary Contact Email' },
      { field: 'totalUnits', label: 'Total Units / Parcels' },
    ];
    return required.filter((r) => !assessmentData[r.field]).map((r) => r.label + ' is required');
  };

  const getProgress = () => {
    if (currentStep <= TOTAL_STEPS) {
      return (currentStep / TOTAL_STEPS) * 100;
    } else {
      return 100; // Discovery call scheduling is bonus step
    }
  };

  const renderCurrentStep = () => {
    const commonProps = {
      data: assessmentData,
      onUpdate: updateAssessmentData,
      tier,
    };

    switch (currentStep) {
      case 1:
        return <CommunityProfile {...commonProps} />;
      case 2:
        return <FinancialAssessment {...commonProps} />;
      case 3:
        return <PainPointsAnalysis {...commonProps} />;
      case 4:
        return <TechnologyAssessment {...commonProps} />;
      case 5:
        return <StakeholderMapping {...commonProps} />;
      case 6:
        return <ImplementationReadiness {...commonProps} />;
      case 7:
        return <VisionGoals {...commonProps} />;
      case 8:
        return <AddOnsConfirmation {...commonProps} />;
      case 9:
        return (
          <DiscoveryCallScheduler
            assessmentData={assessmentData}
            onScheduled={handleDiscoveryCallScheduled}
            onBack={() => setCurrentStep(TOTAL_STEPS)}
          />
        );
      case 10:
        return (
          <ProposalGenerator
            assessmentData={assessmentData as CommunityAssessmentData}
            discoveryCallData={discoveryCallData}
            onComplete={handleProposalComplete}
          />
        );
      default:
        return <CommunityProfile {...commonProps} />;
    }
  };

  const getTierColor = () => {
    switch (tier) {
      case 'silver':
        return 'from-gray-600 to-gray-800';
      case 'gold':
        return 'from-blue-600 to-blue-800';
      case 'custom':
        return 'from-purple-600 to-purple-800';
      default:
        return 'from-gray-600 to-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-xs border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="text-sm text-gray-500">
              {currentStep <= TOTAL_STEPS
                ? `Step ${currentStep} of ${TOTAL_STEPS}: ${stepTitles[currentStep - 1]}`
                : `${stepTitles[currentStep - 1]}`}
            </div>
            <Button
              variant="ghost"
              onClick={() => communityAssessmentService.saveProgress(assessmentData, currentStep)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <Save className="w-4 h-4" />
              Save Progress
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <Progress value={getProgress()} className="h-2" />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Getting Started</span>
              <span>{Math.round(getProgress())}% Complete</span>
              <span>Ready for Discovery Call</span>
            </div>
          </div>

          {/* Selected Add-ons Indicator */}
          {assessmentData.selectedAddOns && assessmentData.selectedAddOns.length > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
              <ShoppingCart className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Selected Add-ons:</span>
              <div className="flex flex-wrap gap-1">
                {assessmentData.selectedAddOns.slice(0, 3).map((addonId) => {
                  const addon = MONTANA_ADDONS_CATALOG.find((a) => a.id === addonId);
                  return addon ? (
                    <span
                      key={addonId}
                      className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs"
                    >
                      {addon.shortLabel || addon.name}
                    </span>
                  ) : null;
                })}
                {assessmentData.selectedAddOns.length > 3 && (
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                    +{assessmentData.selectedAddOns.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Step Indicator */}
      <div className={`bg-linear-to-r ${getTierColor()} text-white py-6`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-4">
              {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                <div key={i} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      i + 1 < currentStep
                        ? 'bg-green-500 text-white'
                        : i + 1 === currentStep
                          ? 'bg-white text-gray-900'
                          : 'bg-white/30 text-white/70'
                    }`}
                  >
                    {i + 1 < currentStep ? <CheckCircle className="w-5 h-5" /> : i + 1}
                  </div>
                  {i < TOTAL_STEPS - 1 && (
                    <div
                      className={`w-8 h-0.5 ${
                        i + 1 < currentStep ? 'bg-green-500' : 'bg-white/30'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-center">
                  {stepTitles[currentStep - 1]}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                {validationErrors.length > 0 && (
                  <div className="mb-6 rounded-md border border-red-300 bg-red-50 text-red-700 p-4 text-sm space-y-1">
                    <p className="font-semibold">Please fix the following before continuing:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {validationErrors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {renderCurrentStep()}
              </CardContent>
            </Card>

            {/* Navigation - Hidden for discovery call step as it has its own navigation */}
            {currentStep !== TOTAL_STEPS_WITH_SCHEDULING && (
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  {currentStep === 1 ? 'Back to Welcome' : 'Previous Step'}
                </Button>

                <Button
                  onClick={handleNext}
                  disabled={isSubmitting}
                  className={`flex items-center gap-2 bg-linear-to-r ${getTierColor()} text-white`}
                >
                  {currentStep === TOTAL_STEPS ? (
                    isSubmitting ? (
                      'Submitting...'
                    ) : (
                      'Complete Assessment'
                    )
                  ) : (
                    <>
                      Next Step <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Help Text */}
            <div className="text-center mt-6">
              <p className="text-sm text-gray-500">
                {/* Need help? <a href="mailto:support@example.com" className="text-blue-600 hover:underline">
                  Contact our support team
                </a> */}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
